/**
 * Validated environment access.
 *
 * Why this exists: reading `import.meta.env.VITE_*` ad hoc across the app means
 * a typo or a missing value fails silently deep in a component. Here we read and
 * validate everything once, fail fast with a clear message, and export a typed,
 * frozen object the rest of the app imports from.
 *
 * Never read import.meta.env outside this file.
 */

function required(key) {
  const value = import.meta.env[key];
  if (value === undefined || value === '') {
    throw new Error(
      `[config/env] Missing required environment variable: ${key}. ` +
        `Copy .env.example to .env.local and fill it in.`,
    );
  }
  return value;
}

function optional(key, fallback = '') {
  const value = import.meta.env[key];
  return value === undefined || value === '' ? fallback : value;
}

/**
 * A true/false switch that distinguishes "said false" from "said nothing".
 *
 * Returns null when unset, so a caller can tell an explicit `false` apart from
 * the default — loginMode.js depends on that, since "local=false" means "not the
 * local form" while an unset flag means no opinion. Anything other than
 * true/false throws: `VITE_LOCAL_LOGIN=yes` silently reading as "unset" would
 * leave you on the wrong screen with no hint why.
 *
 * @returns {boolean | null}
 */
function optionalBool(key) {
  const raw = import.meta.env[key];
  if (raw === undefined || raw === '') return null;
  const value = String(raw).trim().toLowerCase();
  if (value === 'true') return true;
  if (value === 'false') return false;
  throw new Error(`[config/env] ${key} must be 'true' or 'false', got: ${raw}`);
}

function toInt(value, key) {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) {
    throw new Error(`[config/env] ${key} must be an integer, got: ${value}`);
  }
  return n;
}

/**
 * A comma-separated setting as a frozen list: trimmed, lower-cased, blanks
 * dropped. Unset or empty is an empty list.
 */
function optionalList(key) {
  return Object.freeze(
    String(import.meta.env[key] ?? '')
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean),
  );
}

const APP_MODE = optional('VITE_APP_MODE', '').toLowerCase();
if (APP_MODE && !['cca', 'eia'].includes(APP_MODE)) {
  throw new Error(`[config/env] VITE_APP_MODE must be 'cca', 'eia', or empty. Got: ${APP_MODE}`);
}

/*
 * Mocks are ON by default.
 *
 * This is now a per-feature question, not an app-wide one. Features whose
 * endpoints are ported (notifications) talk to the real API unconditionally and
 * ignore this flag. Features still being built (recommendations) have no live
 * endpoint to call yet, so they resolve against their own mocks until they are.
 *
 * Set VITE_USE_MOCKS=false once the remaining features are ported.
 */
const USE_MOCKS = optional('VITE_USE_MOCKS', 'true').toLowerCase() !== 'false';

export const env = Object.freeze({
  apiBaseUrl: required('VITE_API_BASE_URL'),
  // Common Services (legacy VITE_CS_API) — identity, policy types, org data.
  // Optional: nothing needs it until those endpoints are ported.
  csApiBaseUrl: optional('VITE_CS_API', ''),
  /*
   * The Common Services APP (not its API) — where the Advisory login sends the
   * browser for SSO. Names are legacy's, kept on purpose so the same values work
   * in both apps' env files, even though "OKTA_POSTLOGOUT_REDIRECT_URI" is a
   * misleading name for what is really the Common Services host. It differs per
   * environment (dev.epycadvisory.amd.com in dev), so it cannot be hard-coded.
   *
   * All optional: nothing needs them until the Advisory login is the active one,
   * and HomePage reports "not configured" itself rather than failing the boot of
   * a developer who only ever uses the local form.
   */
  csHomeUrl: optional('VITE_OKTA_POSTLOGOUT_REDIRECT_URI', ''),
  csRegisterUrl: optional('VITE_OKTA_REGISTER_URI', ''),
  csLogoutUrl: optional('VITE_OKTA_LOGOUT_LINK', ''),
  // Profile menu → "Profile": Common Services' request-role page (legacy name
  // kept, as above). The ONLY source of that URL — no fallback (user order
  // 2026-10-22); unset -> Profile says it is not available.
  csProfileUrl: optional('VITE_PROFILE_REDIRECT_URI', ''),
  // EIA's Meta Collector button (legacy's name). The ONLY source of that URL —
  // no fallback (user order 2026-10-22).
  metaCollectorUrl: optional('VITE_METACOLLECTOR_URL', ''),
  // Where the app bar logo goes (user order 2026-10-23). Unset -> the logo
  // stays an in-app link to the landing Home page.
  advisoryUrl: optional('VITE_ADVISORY', ''),
  /*
   * Extra host names or addresses to recognise as CCA, comma-separated, for a
   * deployment whose host name does not contain "cca" (legacy reached two CCA
   * servers by IP address). They are set per environment HERE, never in source:
   * a hard-coded address in variants.config.js failed the Jenkins security scan.
   * Empty by default — a host named for CCA needs nothing.
   */
  ccaExtraHosts: optionalList('VITE_CCA_EXTRA_HOSTS'),
  /*
   * NO Okta client id / secret here. Legacy sent them as Basic auth to Common
   * Services' /getRefreshToken (apiservice.js:113-127), which compiled the secret
   * into the public bundle; the backend holds them itself now, so the refresh
   * (services/sessionRefresh.js) sends only the refresh token. Don't add them
   * back — every VITE_ value is public.
   */
  /*
   * Manual login-screen switch for non-AMD hosts (see config/loginMode.js).
   * true / false / null (= no opinion). LOCALHOST ONLY — ignored on every other
   * host, where the Advisory login is the only one that exists.
   */
  advisoryLoginOverride: optionalBool('VITE_ADVISORY_LOGIN'),
  localLoginOverride: optionalBool('VITE_LOCAL_LOGIN'),
  /*
   * Request timeout in ms; 0 = none. NONE by default (user order 2026-10-27),
   * as legacy: none of its axios instances (apiservice.js) sets a timeout, so a
   * slow answer is waited for. The UI used to cut requests off at 30 s and show
   * axios's "timeout of 30000ms exceeded". Set VITE_API_TIMEOUT_MS to bring a
   * limit back (apiClient.js and the token refresh both read it).
   */
  apiTimeoutMs: toInt(optional('VITE_API_TIMEOUT_MS', '0'), 'VITE_API_TIMEOUT_MS'),
  appModeOverride: APP_MODE, // '' means: resolve from URL at runtime
  useMocks: USE_MOCKS,
  telemetryDsn: optional('VITE_TELEMETRY_DSN', ''),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
});
